import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BlogListGridSkeleton } from "@/components/blog/BlogListSkeleton";
import { cn } from "@/lib/utils";

const DEFAULT_ROUTE_CARD_KEYS = [
  "route-card-1",
  "route-card-2",
  "route-card-3",
  "route-card-4",
  "route-card-5",
  "route-card-6",
] as const;
const FAQ_SKELETON_KEYS = [
  "faq-sk-1",
  "faq-sk-2",
  "faq-sk-3",
  "faq-sk-4",
  "faq-sk-5",
  "faq-sk-6",
] as const;
const REPORTS_STAT_KEYS = [
  "reports-stat-1",
  "reports-stat-2",
  "reports-stat-3",
  "reports-stat-4",
] as const;
const SETTINGS_FIELD_KEYS = [
  "settings-field-1",
  "settings-field-2",
  "settings-field-3",
  "settings-field-4",
  "settings-field-5",
] as const;
const FAMILY_MEMBER_KEYS = [
  "family-member-1",
  "family-member-2",
  "family-member-3",
  "family-member-4",
] as const;
const ADMIN_TABLE_ROW_KEYS = [
  "admin-row-1",
  "admin-row-2",
  "admin-row-3",
  "admin-row-4",
  "admin-row-5",
  "admin-row-6",
  "admin-row-7",
  "admin-row-8",
] as const;
const PROTECTED_ROUTE_CARD_KEYS = [
  "protected-card-1",
  "protected-card-2",
  "protected-card-3",
  "protected-card-4",
  "protected-card-5",
  "protected-card-6",
] as const;

/** Default lazy-route fallback — skeleton layout instead of a full-page spinner. */
export function DefaultRouteLoadingFallback() {
  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl space-y-6" aria-busy="true">
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-5 w-full max-w-2xl" />
        <Skeleton className="h-5 w-3/4 max-w-xl" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {DEFAULT_ROUTE_CARD_KEYS.map((skeletonKey) => (
          <Skeleton key={skeletonKey} className="h-40 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}

/** Auth routes while session/OAuth state resolves. */
export function AuthPageLoadingFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      aria-busy="true"
      aria-label="Loading sign in"
    >
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="h-8 w-48 mx-auto" />
        <Skeleton className="h-4 w-64 mx-auto" />
        <Skeleton className="h-11 w-full rounded-md" />
        <Skeleton className="h-11 w-full rounded-md" />
        <Skeleton className="h-11 w-full rounded-md" />
      </div>
    </div>
  );
}

/** Blog list/search routes while the JS chunk loads. */
export function BlogRouteLoadingFallback() {
  return (
    <div className="container max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="space-y-4 mb-8">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-5 w-96 max-w-full" />
        <Skeleton className="h-10 w-full max-w-md" />
      </div>
      <BlogListGridSkeleton />
    </div>
  );
}

/** FAQ page while the JS chunk loads. */
export function FaqRouteLoadingFallback() {
  return (
    <div className="container max-w-3xl mx-auto px-4 py-16 space-y-4">
      <Skeleton className="h-10 w-64 mx-auto" />
      <Skeleton className="h-5 w-full max-w-lg mx-auto" />
      <div className="space-y-3 pt-8">
        {FAQ_SKELETON_KEYS.map((skeletonKey) => (
          <Skeleton key={skeletonKey} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/** Dashboard widget card skeleton (Upcoming Payments, Session Info, etc.). */
export function DashboardWidgetSkeleton() {
  return (
    <Card aria-busy="true" aria-label="Loading widget">
      <CardHeader>
        <Skeleton className="h-4 w-1/2" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </CardContent>
    </Card>
  );
}

/** Dashboard chart / donut skeleton. */
export function DashboardChartSkeleton({ className }: Readonly<{ className?: string }>) {
  return (
    <Card aria-busy="true" aria-label="Loading chart" className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-1/2" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-64 w-full rounded-xl" />
      </CardContent>
    </Card>
  );
}

/** Transactions page chart panel skeleton. */
export function TransactionsChartSkeleton({
  heightClass = "h-[300px]",
  className,
}: Readonly<{
  heightClass?: string;
  className?: string;
}>) {
  return (
    <div
      className={cn("border rounded-md p-4 sm:p-6", className)}
      aria-busy="true"
      aria-label="Loading chart"
    >
      <Skeleton className={cn("w-full", heightClass)} />
    </div>
  );
}

/** Reports page — stat cards + chart skeleton. */
export function ReportsRouteLoadingFallback() {
  return (
    <div
      className="flex min-h-screen w-full bg-white"
      aria-busy="true"
      aria-label="Loading reports"
    >
      <Skeleton className="hidden lg:block w-64 shrink-0 min-h-screen rounded-none" />
      <div className="flex flex-col flex-1 min-h-screen p-4 lg:p-6 space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {REPORTS_STAT_KEYS.map((skeletonKey) => (
            <Skeleton key={skeletonKey} className="h-28 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    </div>
  );
}

/** Settings page — tab bar + form field skeletons. */
export function SettingsRouteLoadingFallback() {
  return (
    <div
      className="flex min-h-screen w-full bg-white"
      aria-busy="true"
      aria-label="Loading settings"
    >
      <Skeleton className="hidden lg:block w-64 shrink-0 min-h-screen rounded-none" />
      <div className="flex flex-col flex-1 min-h-screen p-4 lg:p-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-full max-w-lg" />
        <div className="space-y-3 pt-4">
          {SETTINGS_FIELD_KEYS.map((skeletonKey) => (
            <Skeleton key={skeletonKey} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Family hub — member cards skeleton. */
export function FamilyRouteLoadingFallback() {
  return (
    <div className="flex min-h-screen w-full bg-white" aria-busy="true" aria-label="Loading family">
      <Skeleton className="hidden lg:block w-64 shrink-0 min-h-screen rounded-none" />
      <div className="flex flex-col flex-1 min-h-screen p-4 lg:p-6 space-y-4">
        <Skeleton className="h-10 w-40" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FAMILY_MEMBER_KEYS.map((skeletonKey) => (
            <Skeleton key={skeletonKey} className="h-36 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Admin console — table rows skeleton. */
export function AdminRouteLoadingFallback() {
  return (
    <div className="flex min-h-screen w-full bg-white" aria-busy="true" aria-label="Loading admin">
      <Skeleton className="hidden lg:block w-64 shrink-0 min-h-screen rounded-none" />
      <div className="flex flex-col flex-1 min-h-screen p-4 lg:p-6 space-y-4">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <div className="space-y-2 pt-2">
          {ADMIN_TABLE_ROW_KEYS.map((skeletonKey) => (
            <Skeleton key={skeletonKey} className="h-10 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Protected app routes — keeps sidebar/header shell visible while lazy chunks load. */
export function ProtectedRouteLoadingFallback() {
  return (
    <div className="flex min-h-screen w-full bg-white" aria-busy="true" aria-label="Loading page">
      <Skeleton className="hidden lg:block w-64 shrink-0 min-h-screen rounded-none" />
      <div className="flex flex-col flex-1 min-h-screen p-4 lg:p-6 space-y-4">
        <Skeleton className="h-10 w-full max-w-xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROTECTED_ROUTE_CARD_KEYS.map((skeletonKey) => (
            <Skeleton key={skeletonKey} className="h-28 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    </div>
  );
}
